import {addDays} from 'date-fns';
import {
  Alert,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
  type FormControl,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Text, VStack} from 'platform/foundation';

import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {
  CampaignRequestBody,
  CampaignResponseBody,
  CampaignStatusSchema,
  useCreateCampaignMutation,
  useGetWatermarkListQuery,
  useUpdateCampaignMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useGetUserOptions, useWarrantyProgramOptions} from '@dms/shared';

import {ApiError, getApiDateString, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {openRepublishAdsConfirmDialog} from '../../Campaigns/utils/openRepublishAdsConfirmDialog';
import {campaignFormSchema, type CampaignFormValues} from '../campaignFormSchema';
import {getCampaignStatus} from '../utils/getCampaignStatus';
import {hasChangedRelevantRepublishValues} from '../utils/hasChangedRelevantRepublishValues';
import {CampaignCollisionList} from './CampaignCollisionList';
import {CampaignStatus} from './CampaignStatus';
import {CampaignVehicleSelectionSection} from './CampaignVehicleSelectionSection';
import {PromoPhotosField} from './PromoPhotosField';
import {WatermarkPresetPreview} from './WatermarkPresetPreview';

const getDefaultStartDate = () => addDays(new Date(), 1);

interface CampaignFormProps extends RequiredTestIdProps {
  campaign?: CampaignResponseBody;
  currentUserId?: string;
  onDiscard: () => void;
  onSuccess: () => void;
}

interface CampaignFormFieldsProps extends RequiredTestIdProps {
  campaign?: CampaignResponseBody;
  control: FormControl<CampaignFormValues>;
  formApi: UseFormReturn<CampaignFormValues>;
  onDiscard: () => void;
}

export function CampaignForm(props: CampaignFormProps) {
  const [createCampaign] = useCreateCampaignMutation();
  const [updateCampaign] = useUpdateCampaignMutation();

  const defaultValues: Partial<CampaignFormValues> = {
    ...props.campaign,
    ownerId: props.campaign?.ownerId ?? props.currentUserId,
    validFrom: isNotNil(props.campaign)
      ? parseDate(props.campaign.validFrom)
      : getDefaultStartDate(),
    validTo: isNotNil(props.campaign) ? parseDate(props.campaign.validTo) : undefined,
    discount: props.campaign?.discount
      ? {
          type: props.campaign?.discount?.type,
          amount: Number(props.campaign?.discount?.amount),
        }
      : null,
    promoPhotos: props.campaign?.promoPhotos ?? [],
    filter: props.campaign?.filter ?? {},
  };

  const handleSubmit: FormSubmitHandler<CampaignFormValues> = (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const submittedStatus = getCampaignStatus(values.validFrom, values.validTo);
    const savedStatus = props.campaign?.status;

    const shouldConfirmRepublish =
      submittedStatus === CampaignStatusSchema.enum.ACTIVE &&
      (savedStatus !== CampaignStatusSchema.enum.ACTIVE ||
        hasChangedRelevantRepublishValues(values, defaultValues));

    const submitCampaign = (republishAds: boolean) => {
      const requestBody: CampaignRequestBody = {
        ...values,
        republishAds,
        validFrom: getApiDateString(values.validFrom),
        validTo: getApiDateString(values.validTo),
        filter: values.isAppliedToAllVehicles ? {} : (values.filter ?? {}),
        promoPhotos: values.promoPhotos ?? [],
        discount:
          isNotNil(values.discount?.type) && isNotNil(values.discount?.amount)
            ? {
                type: values.discount.type,
                amount: values.discount.amount.toString(),
              }
            : null,
        keepExistingPromoPhotos: values.keepExistingPromoPhotos ?? false,
        watermarkId: values.watermarkId ?? null,
        warrantyProgramId: values.warrantyProgramId ?? null,
        isAppliedToAllVehicles: values.isAppliedToAllVehicles ?? false,
      };

      const successMessage = isNotNil(props.campaign)
        ? i18n.t('general.notifications.changesSuccessfullySaved')
        : i18n.t('general.notifications.successfullyCreated');

      const handleSuccess = () => {
        showNotification.success(successMessage);
        reset(undefined, {keepValues: true});

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        props.onSuccess();
      };

      const submitAction = isNotNil(props.campaign)
        ? updateCampaign({id: props.campaign.id, updateCampaignRequestBody: requestBody})
        : createCampaign({createCampaignRequestBody: requestBody});

      return submitAction
        .unwrap()
        .then(handleSuccess)
        .catch((error: ApiError) =>
          handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
        );
    };

    if (!shouldConfirmRepublish) {
      return submitCampaign(false);
    }

    return openRepublishAdsConfirmDialog({
      isCreating: isNil(props.campaign),
      onSelect: submitCampaign,
      onDismiss: () => {
        // reportApiFailure is the blocker's only "don't navigate away" signal.
        blockerContext?.reportApiFailure?.();
      },
    });
  };

  return (
    <Form<CampaignFormValues>
      experimentalZodSchema={campaignFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, _navigateWithoutBlocker, withoutBlocker) => (
        <CampaignFormFields
          campaign={props.campaign}
          control={control}
          formApi={formApi}
          onDiscard={() => withoutBlocker(props.onDiscard)}
          data-testid={props['data-testid']}
        />
      )}
    </Form>
  );
}

function CampaignFormFields(props: CampaignFormFieldsProps) {
  const [validTo, validFrom, watermarkId, promoPhotos, discountType, warrantyProgramId] = useWatch({
    control: props.control,
    name: [
      'validTo',
      'validFrom',
      'watermarkId',
      'promoPhotos',
      'discount.type',
      'warrantyProgramId',
    ],
  });

  const {userOptions, isLoading: isUsersLoading} = useGetUserOptions({
    withAvailabilitySummary: false,
  });
  const {data: watermarks, isLoading: isWatermarkListLoading} = useGetWatermarkListQuery();

  const {warrantyProgramOptions, isLoading: isWarrantyLoading} =
    useWarrantyProgramOptions(warrantyProgramId);

  const watermarkOptions =
    watermarks?.map((watermark) => ({value: watermark.id, label: watermark.name})) ?? [];

  const discountTypeOptions = [
    {label: i18n.t('entity.salesCampaign.discountType.percent'), value: 'PERCENT'},
    {label: i18n.t('entity.salesCampaign.discountType.absolute'), value: 'ABSOLUTE'},
  ];

  return (
    <VStack spacing={6}>
      <Alert
        variant="info"
        message={i18n.t('entity.salesCampaign.hints.visualBatchInfo')}
        data-testid={suffixTestId('visualBatchInfo', props)}
      />

      <Grid columns={2} spacing={4}>
        <GridItem span={2}>
          <FormField
            control={props.control}
            type="text"
            name="name"
            label={i18n.t('entity.salesCampaign.labels.name')}
            isRequired
            data-testid={suffixTestId('name', props)}
          />
        </GridItem>

        <FormField
          control={props.control}
          type="choice"
          name="ownerId"
          label={i18n.t('entity.salesCampaign.labels.owner')}
          options={userOptions}
          isLoading={isUsersLoading}
          isRequired
          menuInPortal
          helperText={i18n.t('entity.salesCampaign.hints.ownerDefault')}
          data-testid={suffixTestId('owner', props)}
        />

        <CampaignStatus
          validFrom={validFrom}
          validTo={validTo}
          data-testid={suffixTestId('status', props)}
        />

        <FormField
          control={props.control}
          type="date"
          name="validFrom"
          label={i18n.t('entity.salesCampaign.labels.validFrom')}
          isRequired
          helperText={i18n.t('entity.salesCampaign.hints.validFrom')}
          data-testid={suffixTestId('validFrom', props)}
        />
        <FormField
          control={props.control}
          type="date"
          name="validTo"
          label={i18n.t('entity.salesCampaign.labels.validTo')}
          isRequired
          data-testid={suffixTestId('validTo', props)}
        />
      </Grid>

      <Separator spacing={0} />
      <VStack spacing={6}>
        <VStack spacing={1}>
          <Heading size={4}>{i18n.t('entity.salesCampaign.sections.visualElements')}</Heading>
          <Text size="small" color="tertiary">
            {i18n.t('entity.salesCampaign.sections.visualElementsDescription')}
          </Text>
        </VStack>

        <Grid columns={2} spacing={4}>
          <FormField
            control={props.control}
            type="choice"
            name="watermarkId"
            label={i18n.t('entity.salesCampaign.labels.watermarkPreset')}
            options={watermarkOptions}
            isLoading={isWatermarkListLoading}
            menuInPortal
            helperText={i18n.t('entity.salesCampaign.hints.watermarkPreset')}
            data-testid={suffixTestId('watermark', props)}
          />
          <WatermarkPresetPreview
            watermarkId={watermarkId ?? null}
            data-testid={suffixTestId('watermarkPreview', props)}
          />
          <GridItem span={2}>
            <PromoPhotosField
              value={promoPhotos ?? []}
              onChange={(next) => {
                props.formApi.setValue('promoPhotos', next, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
              data-testid={suffixTestId('promoPhotos', props)}
            />
          </GridItem>
          <GridItem span={2}>
            <FormField
              control={props.control}
              type="checkbox"
              name="keepExistingPromoPhotos"
              label={i18n.t('entity.salesCampaign.labels.keepExistingPromoPhotos')}
              helperText={i18n.t('entity.salesCampaign.hints.keepExistingPromoPhotos')}
              data-testid={suffixTestId('keepExistingPromoPhotos', props)}
            />
          </GridItem>
        </Grid>
      </VStack>

      <Separator spacing={0} />
      <VStack spacing={6}>
        <Heading size={4}>{i18n.t('entity.salesCampaign.sections.discountAndWarranty')}</Heading>
        <Grid columns={2} spacing={4}>
          <GridItem>
            <VStack spacing={1}>
              <HStack spacing={2}>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    type="choice"
                    name="discount.type"
                    label={i18n.t('entity.salesCampaign.labels.discount')}
                    options={discountTypeOptions}
                    menuInPortal
                    data-testid={suffixTestId('discountType', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    control={props.control}
                    type="number"
                    name="discount.amount"
                    decimalPlaces={2}
                    label={i18n.t('entity.salesCampaign.labels.discountAmount')}
                    suffix={discountType === 'PERCENT' ? '%' : undefined}
                    data-testid={suffixTestId('discountAmount', props)}
                  />
                </Box>
              </HStack>
              <Text
                size="xSmall"
                color="tertiary"
                data-testid={suffixTestId('discountWarrantyInfo', props)}
              >
                {i18n.t('entity.salesCampaign.hints.discountWarrantyInfo')}
              </Text>
            </VStack>
          </GridItem>
          <FormField
            control={props.control}
            type="choice"
            name="warrantyProgramId"
            label={i18n.t('entity.salesCampaign.labels.warrantyProgram')}
            options={warrantyProgramOptions}
            isLoading={isWarrantyLoading}
            menuInPortal
            data-testid={suffixTestId('warranty', props)}
          />
        </Grid>
      </VStack>

      <Separator spacing={0} />
      <CampaignCollisionList
        control={props.control}
        campaignId={props.campaign?.id}
        data-testid={suffixTestId('collision', props)}
      />
      <CampaignVehicleSelectionSection
        control={props.control}
        formApi={props.formApi}
        data-testid={suffixTestId('vehicleSelection', props)}
      />
      <SettingsFooter
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            onClick: props.onDiscard,
            isDisabled: props.formApi.formState.isSubmitting,
            title: i18n.t('general.actions.discardChanges'),
            'data-testid': suffixTestId('discard', props),
          },
          {
            type: 'form-button',
            control: props.control,
            buttonType: 'submit',
            title: i18n.t('general.actions.saveAndClose'),
            'data-testid': suffixTestId('submit', props),
          },
        ]}
        data-testid={suffixTestId('footer', props)}
      />
    </VStack>
  );
}
